import classNames from 'classnames'
import type { ReactNode } from 'react'

import { ORDER_PRODUCT_STATUSES, ORDER_STATUSES } from '@/constants'
import type { OrderInterface, PaymentTypeInterface } from '@/types'

import type { OrderTotals } from '../lib/orderTotals'
import cn from './OrderInfoCards.module.scss'

type Described = { description: string }

type OrderModel = Pick<
  OrderInterface['orderModel'],
  'code' | 'orderStatus' | 'createdAt' | 'deliveryAddress' | 'comment'
> & {
  authorShortModel?: Described
  clientShortModel?: Described
  constructionSiteShortModel?: Described
  contractShortModel?: Described
  paymentTypeDto?: Pick<PaymentTypeInterface, 'paymentType'>
}

type OrderInfoCardsProps = {
  order: OrderModel
  totals?: OrderTotals
  isOrganisationSelected: boolean
}

type InfoRow = {
  label: string
  value: ReactNode
}

const EMPTY_VALUE = '—'

const getStatusLabel = (order: OrderModel) =>
  ORDER_PRODUCT_STATUSES[order.orderStatus] || ORDER_STATUSES[order.orderStatus]

const getPaymentLabel = (order: OrderModel) =>
  order.paymentTypeDto?.paymentType === 'CASH' ? 'Наличные' : 'Безналичный'

function InfoCard({ title, rows }: { title: string; rows: InfoRow[] }) {
  return (
    <section className={cn.card}>
      <h3 className={cn.title}>{title}</h3>
      <dl className={cn.rows}>
        {rows.map((row) => (
          <div className={cn.row} key={row.label}>
            <dt className={cn.label}>{row.label}</dt>
            <dd className={cn.value}>{row.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

function TotalsCard({ totals }: { totals: OrderTotals }) {
  return (
    <section className={classNames(cn.card, cn.totalsCard)}>
      <h3 className={cn.title}>Сумма заказа</h3>
      <dl className={cn.rows}>
        <div className={cn.row}>
          <dt className={cn.label}>Позиций</dt>
          <dd className={cn.value}>{totals.itemsCount}</dd>
        </div>
        <div className={cn.row}>
          <dt className={cn.label}>Итого ОПТ, BYN</dt>
          <dd className={cn.totalValue}>{totals.opt}</dd>
        </div>
        <div className={cn.row}>
          <dt className={cn.label}>Итого РРЦ, BYN</dt>
          <dd className={cn.totalValue}>{totals.rrc}</dd>
        </div>
      </dl>
    </section>
  )
}

function OrderInfoCards({
  order,
  totals,
  isOrganisationSelected,
}: OrderInfoCardsProps) {
  const orderRows: InfoRow[] = [
    { label: '№ заказа', value: order.code },
    { label: 'Статус', value: getStatusLabel(order) },
    {
      label: 'Дата',
      value: new Date(order.createdAt).toLocaleDateString(),
    },
    {
      label: 'Автор',
      value: order.authorShortModel?.description || EMPTY_VALUE,
    },
  ]

  const deliveryRows: InfoRow[] = [
    ...(order.constructionSiteShortModel
      ? [
          {
            label: 'Объект',
            value: order.constructionSiteShortModel.description,
          },
        ]
      : []),
    order.deliveryAddress
      ? { label: 'Адрес доставки', value: order.deliveryAddress }
      : { label: 'Тип доставки', value: 'Самовывоз' },
  ]

  const paymentRows: InfoRow[] = [
    ...(order.contractShortModel
      ? [
          {
            label: 'Договор',
            value: order.contractShortModel.description,
          },
        ]
      : []),
    { label: 'Способ оплаты', value: getPaymentLabel(order) },
  ]

  return (
    <div
      className={classNames(
        cn.info,
        isOrganisationSelected && cn.organisationSelected
      )}
    >
      {order.clientShortModel && (
        <div className={cn.client}>
          <span className={cn.clientLabel}>Клиент</span>
          <h2 className={cn.clientName}>
            {order.clientShortModel.description}
          </h2>
        </div>
      )}
      <div className={cn.cards}>
        {totals && <TotalsCard totals={totals} />}
        <InfoCard title="Заказ" rows={orderRows} />
        <InfoCard title="Доставка" rows={deliveryRows} />
        <InfoCard title="Оплата" rows={paymentRows} />
        <section className={cn.card}>
          <h3 className={cn.title}>Комментарий</h3>
          <p className={cn.comment}>{order.comment || EMPTY_VALUE}</p>
        </section>
      </div>
    </div>
  )
}

export default OrderInfoCards
